import React, { ReactNode } from 'react';

import { Table } from '@phonepe/enigma-ui';
import cx from 'classnames';
import { isEmpty } from 'lodash';

import { IDataTableProps, IPagination } from './DataTable.types';
import { ANALYTICS_EVENT_TYPES, PAGINATION_CLICKED } from '@shared/constants';
import { Button, Pagination } from '@shared/components';

import styles from './DataTable.module.scss';

interface TablePaginationProps extends IPagination {
	paginationType: 'default' | 'cursor';
	hasNextPage: boolean;
	onPageChange: (pageNumber: number, pageSize: number) => void;
}

const TablePagination: React.FC<TablePaginationProps> = ({
	pageNumber,
	pageSize,
	totalRows,
	paginationType,
	hasNextPage,
	onPageChange,
}) => {
	const renderCustomPagination = (page: number, type: string): ReactNode => {
		switch (type) {
			case 'prev':
				return (
					<Button type="secondary" disabled={pageNumber === 1}>
						Prev
					</Button>
				);
			case 'next':
				return (
					<Button type="secondary" disabled={!hasNextPage}>
						Next
					</Button>
				);
			case 'page':
				return page === pageNumber ? <span>{pageNumber}</span> : null;
			default:
				return null;
		}
	};

	return (
		<Pagination
			className={cx(styles.paginator, {
				[styles.customPagination]: paginationType === 'cursor',
			})}
			currentPage={pageNumber}
			totalRows={totalRows}
			pageSize={pageSize}
			onPageChange={onPageChange}
			{...(paginationType === 'cursor' && {
				itemRender: renderCustomPagination,
			})}
		/>
	);
};

const DataTable = <T,>(props: IDataTableProps<T>) => {
	const {
		className,
		rowHeight = 72,
		onPageChange,
		pagination,
		emptyView,
		paginationType = 'default',
		hasNextPage = false,
		sendAnalyticsEvent,
		...rest
	} = props;

	const shouldPaginate =
		!isEmpty(pagination) && pagination.totalRows > pagination.pageSize;

	function changePage(pageNumber: number, pageSize: number) {
		sendAnalyticsEvent?.({
			type: ANALYTICS_EVENT_TYPES.CLICK,
			data: {
				name: `${rest?.rowUniqueKey ?? ''}_${PAGINATION_CLICKED}`,
				value: JSON.stringify({
					pageName: rest?.rowUniqueKey ?? '',
					pageNumber,
					pageSize,
				}),
			},
		});
		onPageChange?.({
			pageNumber,
			pageSize,
			offset: (pageNumber - 1) * pageSize,
		});
	}

	return (
		<>
			<Table<T>
				rowClassName={styles.row}
				columnClassName={styles.column}
				wrapperClassName={cx(styles.tableWrapper, className)}
				rowHeight={rowHeight}
				emptyView={emptyView}
				{...rest}
			/>
			{shouldPaginate && (
				<TablePagination
					pageNumber={pagination.pageNumber}
					pageSize={pagination.pageSize}
					totalRows={pagination.totalRows}
					paginationType={paginationType}
					hasNextPage={hasNextPage}
					onPageChange={changePage}
				/>
			)}
		</>
	);
};

export default DataTable;
